import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import { ModalShell } from "../../../atoms/ModalShell";
import { CustomButton } from "../../../atoms/CustomButton";
import { Copy } from "../../../atoms/Copy";
import { NoTranslate } from "../../../atoms/NoTranslate";
import { Spinner } from "../../../atoms/Spinner";
import PartnerAdminService, {
  LedgerLine,
  PartnerClient,
  PartnerPayout,
  PartnerPayoutMethod,
  PartnerStatement,
  PayoutDestination,
} from "../../../../services/partnerAdmin";
import {
  connectStatusBadge,
  formatAmount,
  formatDay,
  ledgerKindLabel,
  ledgerStateBadge,
  planLabel,
  PROGRAM_PLANS,
  tierLabel,
} from "./partnerLabels";

const FIELD =
  "w-full rounded-md border border-gray-300 bg-white p-2.5 text-base text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500 md:py-2 md:text-sm";

interface PartnerLedgerSectionProps {
  customerId: string;
  entityId: string;
  partnerId: string;
  isDistributor: boolean;
  clients: PartnerClient[];
}

const CURRENCIES = ["USD", "EUR", "CHF"];

const emptyLine = () => ({
  client: "",
  amount: "",
  currency: "",
  planCode: "",
  paidOn: moment().format("YYYY-MM-DD"),
  reason: "",
  subscriptionId: "",
  invoiceId: "",
});

const errorMessage = (error: any) =>
  error?.response?.data?.message || error?.message || "Request failed";

const payoutLine = (payout: PartnerPayout) =>
  payout.method === "stripe_connect"
    ? `Stripe transfer ${payout.reference ?? ""} to ${
        payout.destination.stripe_account_id ?? ""
      }`
    : payout.method === "paypal"
    ? `PayPal payment ${payout.reference ?? ""} to ${
        payout.destination.paypal_email ?? ""
      }`
    : `Transfer ${payout.reference ?? ""} to ${payout.destination.iban ?? ""}`;

/**
 * What SUM owes the partner: every rebate or commission with its state, the balance per currency, the payouts made,
 * and the payout of a due balance: a Stripe Connect transfer PayMod makes to a distributor's active account, or a bank
 * transfer, or a PayPal payment to an affiliate, root makes first and records here. Root also adds by hand the lines
 * no event creates, a Large Business commission once the prospect's declaration is checked.
 */
const PartnerLedgerSection: React.FC<PartnerLedgerSectionProps> = ({
  customerId,
  entityId,
  partnerId,
  isDistributor,
  clients,
}) => {
  const [statement, setStatement] = useState<PartnerStatement | null>(null);
  const [loading, setLoading] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [currency, setCurrency] = useState("");
  const [destination, setDestination] = useState<PayoutDestination | null>(
    null
  );
  const [method, setMethod] = useState<PartnerPayoutMethod>("bank_transfer");
  const [executedOn, setExecutedOn] = useState(moment().format("YYYY-MM-DD"));
  const [reference, setReference] = useState("");
  const [accountingDocument, setAccountingDocument] = useState("");
  const [paying, setPaying] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [newLine, setNewLine] = useState(emptyLine());
  const [adding, setAdding] = useState(false);
  const [toCancel, setToCancel] = useState<LedgerLine | null>(null);
  const [canceling, setCanceling] = useState(false);

  const clientNames = useMemo(
    () =>
      Object.fromEntries(
        clients.map((client) => [client.entity_id, client.entity_name])
      ),
    [clients]
  );

  const currentClients = useMemo(
    () => clients.filter((client) => !client.detached_at),
    [clients]
  );

  const load = async () => {
    setLoading(true);
    try {
      setStatement(
        await PartnerAdminService.statement(customerId, entityId, partnerId)
      );
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setStatement(null);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerId]);

  const payable = Object.entries(statement?.balances ?? {}).filter(
    ([, balance]) => balance.payable
  );

  const reconcile = async () => {
    setReconciling(true);
    try {
      const summary = await PartnerAdminService.reconcile(
        customerId,
        entityId,
        partnerId
      );
      toast.success(
        `Caught up: ${summary.created} line(s) added, ${summary.canceled} cancelled, ${summary.held} held, ${summary.released} released, ${summary.reopened} reopened from a reversed transfer`
      );
      await load();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setReconciling(false);
    }
  };

  const openPay = async () => {
    await load();
    setCurrency(payable[0]?.[0] ?? "");
    setReference("");
    setAccountingDocument("");
    setExecutedOn(moment().format("YYYY-MM-DD"));
    setDestination(null);
    setMethod("bank_transfer");
    setPayOpen(true);
    try {
      const found = await PartnerAdminService.payoutDestination(
        customerId,
        entityId,
        partnerId
      );
      setDestination(found);
      if (isDistributor) {
        if (
          found.stripe_connect?.status === "ACTIVE" &&
          found.method !== "bank"
        )
          setMethod("stripe_connect");
      } else if (found.paypal && (found.method === "paypal" || !found.bank)) {
        setMethod("paypal");
      }
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  const openAdd = () => {
    setNewLine({
      ...emptyLine(),
      currency: Object.keys(statement?.balances ?? {})[0] ?? "",
    });
    setAddOpen(true);
  };

  const editLine = (field: keyof ReturnType<typeof emptyLine>, value: string) =>
    setNewLine((line) => ({
      ...line,
      [field]: value,
      ...(field === "currency" ? { planCode: "" } : {}),
    }));

  const amount = Number(newLine.amount);
  const lineReady =
    Boolean(newLine.client) &&
    amount > 0 &&
    Boolean(newLine.currency) &&
    Boolean(newLine.paidOn) &&
    newLine.reason.trim().length >= 3;

  const addLine = async () => {
    setAdding(true);
    try {
      const line = await PartnerAdminService.addLine(
        customerId,
        entityId,
        partnerId,
        {
          entity_identification: newLine.client,
          kind: "commission",
          amount: Math.round(amount * 100) / 100,
          currency: newLine.currency,
          paid_on: newLine.paidOn,
          reason: newLine.reason.trim(),
          plan_code: newLine.planCode || undefined,
          subscription_id: newLine.subscriptionId.trim() || undefined,
          invoice_id: newLine.invoiceId.trim() || undefined,
        }
      );
      toast.success(
        `Commission of ${formatAmount(
          line.amount,
          line.currency
        )} added, due ${formatDay(line.due_on ?? "")}`
      );
      setAddOpen(false);
      await load();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setAdding(false);
    }
  };

  const clawedBack = new Set(
    (statement?.lines ?? []).map((line) => line.clawback_of).filter(Boolean)
  );
  const cancellable = (line: LedgerLine) =>
    line.source === "manual" &&
    line.state !== "canceled" &&
    !clawedBack.has(line.id);

  const cancelLine = async () => {
    if (!toCancel) return;
    setCanceling(true);
    try {
      await PartnerAdminService.cancelLine(
        customerId,
        entityId,
        partnerId,
        toCancel.id
      );
      toast.success(
        toCancel.state === "paid"
          ? `Line taken back: ${formatAmount(
              -toCancel.amount,
              toCancel.currency
            )} on the next payout`
          : "Line cancelled"
      );
      setToCancel(null);
      await load();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setCanceling(false);
    }
  };

  const pay = async () => {
    setPaying(true);
    try {
      const payout = await PartnerAdminService.pay(
        customerId,
        entityId,
        partnerId,
        byStripe
          ? {
              currency,
              method,
              accounting_document: accountingDocument.trim() || undefined,
              expected_amount: due,
            }
          : {
              currency,
              method,
              executed_on: executedOn,
              reference: reference.trim(),
              accounting_document: accountingDocument.trim() || undefined,
              expected_amount: due,
            }
      );
      toast.success(
        byStripe
          ? `${formatAmount(
              payout.amount,
              payout.currency
            )} transferred by Stripe`
          : `${byPaypal ? "PayPal payment" : "Payout"} of ${formatAmount(
              payout.amount,
              payout.currency
            )} recorded`
      );
      setPayOpen(false);
      await load();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setPaying(false);
    }
  };

  const title = isDistributor ? "Rebates" : "Commissions";
  const due = statement?.balances[currency]?.due ?? 0;
  const byStripe = method === "stripe_connect";
  const byPaypal = method === "paypal";
  const connect = destination?.stripe_connect ?? null;

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          {title}
        </p>
        <span className="ml-auto flex flex-wrap items-center gap-2">
          <CustomButton
            variant="secondary"
            size="xs"
            title="Catch up"
            tooltip="Rebuild the missing lines from the payments PayMod and Stripe hold"
            disabled={reconciling || loading}
            onClick={reconcile}
          />
          <CustomButton
            variant="secondary"
            size="xs"
            title="Add a line"
            tooltip="A commission no payment event creates, such as a Large Business one"
            disabled={loading || currentClients.length === 0}
            onClick={openAdd}
          />
          <CustomButton
            variant="primary"
            size="xs"
            title="Pay"
            disabled={payable.length === 0 || loading}
            onClick={openPay}
          />
        </span>
      </div>

      {loading && !statement ? (
        <div className="flex justify-center py-6">
          <Spinner size="sm" />
        </div>
      ) : !statement || statement.lines.length === 0 ? (
        <p className="text-sm text-gray-500">Nothing recorded yet.</p>
      ) : (
        <div className="flex flex-col gap-3">
          <dl className="grid grid-cols-[7.5rem_1fr] items-center gap-x-3 gap-y-1.5 text-xs">
            {Object.entries(statement.balances).map(([code, balance]) => (
              <React.Fragment key={code}>
                <dt className="text-gray-500">
                  <NoTranslate>{code}</NoTranslate>
                </dt>
                <dd className="tabular-nums text-gray-700">
                  {`${formatAmount(balance.due, code)} due, ${formatAmount(
                    balance.pending,
                    code
                  )} pending`}
                  {balance.on_hold
                    ? `, ${formatAmount(balance.on_hold, code)} on hold`
                    : ""}
                  {`, ${formatAmount(balance.paid, code)} paid`}
                  {!balance.payable && balance.due > 0
                    ? ` (under the ${formatAmount(
                        balance.threshold,
                        code
                      )} threshold)`
                    : ""}
                </dd>
              </React.Fragment>
            ))}
          </dl>

          <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white">
            {statement.lines.map((line) => (
              <li
                key={line.id}
                className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:items-center"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-gray-900">
                    <NoTranslate>{line.client_entity_id}</NoTranslate>
                  </p>
                  <p className="text-xs text-gray-500">
                    {clientNames[line.client_entity_id]
                      ? `${clientNames[line.client_entity_id]} · `
                      : ""}
                    {ledgerKindLabel(line)}
                    {line.plan_tier ? `, ${tierLabel(line.plan_tier)}` : ""}
                    {line.paid_at ? ` · paid ${formatDay(line.paid_at)}` : ""}
                    {line.state === "paid" && line.paid_on
                      ? ` · paid out ${formatDay(line.paid_on)}`
                      : line.due_on
                      ? ` · due ${formatDay(line.due_on)}`
                      : ""}
                    {line.source === "manual"
                      ? ` · added by hand${
                          line.author ? ` by ${line.author}` : ""
                        }${line.reason ? `: ${line.reason}` : ""}`
                      : ""}
                    {line.canceled_by
                      ? ` · cancelled by ${line.canceled_by}`
                      : ""}
                    {line.cancel_reason ? ` · ${line.cancel_reason}` : ""}
                    {line.state === "on_hold" && line.held_by
                      ? ` · held: ${line.held_by}`
                      : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="tabular-nums text-sm text-gray-900">
                    {formatAmount(line.amount, line.currency)}
                  </span>
                  {ledgerStateBadge(line.state)}
                  {cancellable(line) && (
                    <CustomButton
                      variant="secondary"
                      size="xs"
                      title="Cancel"
                      tooltip="Withdraw this line added by hand"
                      onClick={() => setToCancel(line)}
                    />
                  )}
                </div>
              </li>
            ))}
          </ul>

          {statement.payouts.length > 0 && (
            <>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Payouts{" "}
                <span className="tabular-nums">
                  ({statement.payouts.length})
                </span>
              </p>
              <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white">
                {statement.payouts.map((payout) => (
                  <li key={payout.id} className="px-4 py-3">
                    <p className="text-sm font-medium tabular-nums text-gray-900">
                      {formatAmount(payout.amount, payout.currency)},{" "}
                      {formatDay(payout.executed_on)}
                    </p>
                    <p className="text-xs text-gray-500">
                      <NoTranslate>{payoutLine(payout)}</NoTranslate>
                      {payout.accounting_document
                        ? ` · document ${payout.accounting_document}`
                        : ""}
                      {payout.reversed_at
                        ? ` · reversed, lines due again since ${formatDay(
                            payout.reversed_at
                          )}`
                        : ""}
                    </p>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      <ModalShell
        show={addOpen}
        onClose={() => setAddOpen(false)}
        title="Add a line"
        subtitle="A Large Business commission, once the prospect's declaration is checked. It is due 30 days after the client's payment, then follows the rules of every line."
        footer={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <CustomButton
              variant="secondary"
              title="Cancel"
              onClick={() => setAddOpen(false)}
            />
            <CustomButton
              variant="primary"
              title={
                lineReady
                  ? `Add ${formatAmount(amount, newLine.currency)}`
                  : "Add"
              }
              disabled={!lineReady || adding}
              onClick={addLine}
            />
          </div>
        }
      >
        <div className="flex flex-col gap-3">
          <label className="text-xs text-gray-500">
            Client
            <select
              className={FIELD}
              value={newLine.client}
              onChange={(e) => editLine("client", e.target.value)}
            >
              <option value="">Choose a client</option>
              {currentClients.map((client) => (
                <option key={client.entity_id} value={client.entity_id}>
                  {client.entity_id}
                  {client.entity_name ? ` — ${client.entity_name}` : ""}
                </option>
              ))}
            </select>
          </label>
          <div className="grid grid-cols-[1fr_7rem] gap-2">
            <label className="text-xs text-gray-500">
              Amount
              <input
                type="number"
                min="0.01"
                step="0.01"
                inputMode="decimal"
                className={FIELD}
                value={newLine.amount}
                onChange={(e) => editLine("amount", e.target.value)}
              />
            </label>
            <label className="text-xs text-gray-500">
              Currency
              <select
                className={FIELD}
                value={newLine.currency}
                onChange={(e) => editLine("currency", e.target.value)}
              >
                <option value="">Choose</option>
                {CURRENCIES.map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label className="text-xs text-gray-500">
            Plan, optional
            <select
              className={FIELD}
              value={newLine.planCode}
              disabled={!newLine.currency}
              onChange={(e) => editLine("planCode", e.target.value)}
            >
              <option value="">
                {newLine.currency ? "No plan" : "Choose the currency first"}
              </option>
              {PROGRAM_PLANS.filter(
                (plan) => plan.currency === newLine.currency
              ).map((plan) => (
                <option key={plan.code} value={plan.code}>
                  {planLabel(plan)}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-gray-500">
            Date of the client&apos;s payment
            <input
              type="date"
              className={FIELD}
              value={newLine.paidOn}
              max={moment().format("YYYY-MM-DD")}
              onChange={(e) => editLine("paidOn", e.target.value)}
            />
          </label>
          <label className="text-xs text-gray-500">
            Reason
            <textarea
              className={FIELD}
              rows={3}
              maxLength={500}
              placeholder="Declaration received and acknowledged on, contract signed on (required)"
              value={newLine.reason}
              onChange={(e) => editLine("reason", e.target.value)}
            />
          </label>
          <p className="text-xs text-gray-500">
            With the Stripe subscription or invoice of the payment, a refund or
            a dispute of that payment reaches this line.
          </p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <input
              className={FIELD}
              placeholder="Stripe subscription, sub_… (optional)"
              value={newLine.subscriptionId}
              onChange={(e) => editLine("subscriptionId", e.target.value)}
            />
            <input
              className={FIELD}
              placeholder="Stripe invoice, in_… (optional)"
              value={newLine.invoiceId}
              onChange={(e) => editLine("invoiceId", e.target.value)}
            />
          </div>
        </div>
      </ModalShell>

      <ModalShell
        show={Boolean(toCancel)}
        onClose={() => setToCancel(null)}
        title="Cancel this line?"
        subtitle={
          toCancel?.state === "paid"
            ? `It was paid: ${formatAmount(
                toCancel.amount,
                toCancel.currency
              )} will be deducted from the next payout.`
            : "The line is cancelled and will not be paid."
        }
        footer={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <CustomButton
              variant="secondary"
              title="Keep it"
              onClick={() => setToCancel(null)}
            />
            <CustomButton
              variant="danger"
              title="Cancel the line"
              disabled={canceling}
              onClick={cancelLine}
            />
          </div>
        }
      >
        {toCancel && (
          <p className="text-sm text-gray-700">
            {`${toCancel.client_entity_id}${
              clientNames[toCancel.client_entity_id]
                ? ` (${clientNames[toCancel.client_entity_id]})`
                : ""
            }, ${formatAmount(toCancel.amount, toCancel.currency)}${
              toCancel.reason ? `: ${toCancel.reason}` : ""
            }`}
          </p>
        )}
      </ModalShell>

      <ModalShell
        show={payOpen}
        onClose={() => setPayOpen(false)}
        title={
          byStripe
            ? "Pay by Stripe Connect"
            : byPaypal
            ? "Record a PayPal payment"
            : "Record a payout"
        }
        subtitle={
          byStripe
            ? "PayMod transfers the amount from the platform's Stripe balance to the distributor's Stripe account."
            : byPaypal
            ? "Send the payment from SUM's PayPal account first, then record it here with its transaction ID."
            : "Make the bank transfer first, then record it here."
        }
        footer={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <CustomButton
              variant="secondary"
              title="Cancel"
              onClick={() => setPayOpen(false)}
            />
            <CustomButton
              variant="primary"
              title={
                byStripe
                  ? `Transfer ${formatAmount(due, currency)}`
                  : `Record ${formatAmount(due, currency)}`
              }
              disabled={
                !currency ||
                paying ||
                (byStripe
                  ? connect?.status !== "ACTIVE"
                  : (byPaypal ? !destination?.paypal : !destination?.bank) ||
                    reference.trim().length < 3)
              }
              onClick={pay}
            />
          </div>
        }
      >
        <div className="flex flex-col gap-3">
          <select
            className={FIELD}
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
          >
            {payable.map(([code, balance]) => (
              <option key={code} value={code}>
                {`${code}: ${balance.due} due`}
              </option>
            ))}
          </select>

          {isDistributor && destination && (
            <select
              className={FIELD}
              value={method}
              onChange={(e) => setMethod(e.target.value as PartnerPayoutMethod)}
            >
              <option
                value="stripe_connect"
                disabled={connect?.status !== "ACTIVE"}
              >
                {connect?.status === "ACTIVE"
                  ? "Stripe Connect"
                  : "Stripe Connect, no active account"}
              </option>
              <option value="bank_transfer">Bank transfer made by hand</option>
            </select>
          )}

          {!isDistributor && destination?.paypal && (
            <select
              className={FIELD}
              value={method}
              onChange={(e) => setMethod(e.target.value as PartnerPayoutMethod)}
            >
              <option value="bank_transfer">Bank transfer made by hand</option>
              <option value="paypal">PayPal payment made by hand</option>
            </select>
          )}

          {!destination ? (
            <div className="flex justify-center py-4">
              <Spinner size="sm" />
            </div>
          ) : byStripe ? (
            <dl className="grid grid-cols-[7.5rem_1fr] items-center gap-x-3 gap-y-1.5 rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs">
              <dt className="text-gray-500">Stripe account</dt>
              <dd className="text-gray-700">
                <Copy target={connect?.stripe_account_id ?? ""} />
              </dd>
              <dt className="text-gray-500">Status</dt>
              <dd>{connectStatusBadge(connect?.status ?? "")}</dd>
            </dl>
          ) : byPaypal ? (
            destination.paypal ? (
              <dl className="grid grid-cols-[7.5rem_1fr] items-center gap-x-3 gap-y-1.5 rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs">
                <dt className="text-gray-500">PayPal address</dt>
                <dd className="text-gray-700">
                  <Copy target={destination.paypal.address} />
                </dd>
              </dl>
            ) : (
              <p className="text-sm text-red-700">
                This partner has entered no PayPal address. Ask them to complete
                their payout details in the partner console.
              </p>
            )
          ) : !destination.bank ? (
            <p className="text-sm text-red-700">
              This partner has entered no bank account. Ask them to complete
              their payout details in the partner console.
            </p>
          ) : (
            <dl className="grid grid-cols-[7.5rem_1fr] items-center gap-x-3 gap-y-1.5 rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs">
              <dt className="text-gray-500">Account holder</dt>
              <dd className="text-gray-700">{destination.bank.holder}</dd>
              <dt className="text-gray-500">Bank</dt>
              <dd className="text-gray-700">
                {destination.bank.bank_name || "None"}
              </dd>
              <dt className="text-gray-500">IBAN</dt>
              <dd className="text-gray-700">
                <Copy target={destination.bank.iban} />
              </dd>
              <dt className="text-gray-500">BIC</dt>
              <dd className="text-gray-700">
                <NoTranslate>{destination.bank.bic || "None"}</NoTranslate>
              </dd>
            </dl>
          )}

          {!byStripe && (
            <>
              <label className="text-xs text-gray-500">
                {byPaypal ? "Payment date" : "Transfer date"}
                <input
                  type="date"
                  className={FIELD}
                  value={executedOn}
                  max={moment().format("YYYY-MM-DD")}
                  onChange={(e) => setExecutedOn(e.target.value)}
                />
              </label>
              <input
                className={FIELD}
                placeholder={
                  byPaypal
                    ? "PayPal transaction ID (required)"
                    : "Transfer reference (required)"
                }
                value={reference}
                onChange={(e) => setReference(e.target.value)}
              />
            </>
          )}
          <input
            className={FIELD}
            placeholder={
              isDistributor
                ? "Credit note number, from the accounting tool"
                : "Self-billing document number, from the accounting tool"
            }
            value={accountingDocument}
            onChange={(e) => setAccountingDocument(e.target.value)}
          />
        </div>
      </ModalShell>
    </div>
  );
};

export default PartnerLedgerSection;
